import { useState, useEffect, FormEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { PageHead } from '../seo';
import { validateCredential, recordAccess } from '../../lib/terraAccess';

const PageWrapper = styled.div`
  padding-top: var(--space-16);
  padding-bottom: var(--space-24);
  background: var(--color-background);
  min-height: 100vh;
`;

const Article = styled.article`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: var(--leading-relaxed);

  h1 {
    font-size: var(--text-4xl);
    font-weight: var(--font-semibold);
    letter-spacing: -0.02em;
    margin-bottom: var(--space-6);
  }

  h2 {
    font-size: var(--text-2xl);
    font-weight: var(--font-semibold);
    letter-spacing: -0.015em;
    margin-top: var(--space-16);
    margin-bottom: var(--space-6);
  }

  h3 {
    font-size: var(--text-xl);
    font-weight: var(--font-semibold);
    margin-top: var(--space-10);
    margin-bottom: var(--space-4);
  }

  p {
    font-size: var(--text-base);
    color: var(--color-text-primary);
    margin-bottom: var(--space-5);
    line-height: var(--leading-relaxed);
  }

  ul {
    list-style: disc;
    padding-left: var(--space-6);
    margin-bottom: var(--space-5);
  }

  li {
    font-size: var(--text-base);
    color: var(--color-text-primary);
    margin-bottom: var(--space-2);
    line-height: var(--leading-relaxed);
  }

  code {
    font-family: var(--font-mono);
    font-size: 0.9em;
    background: var(--color-background-secondary);
    padding: 2px 6px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
  }

  pre {
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    overflow-x: auto;
    margin-bottom: var(--space-5);
  }

  pre code {
    background: transparent;
    border: none;
    padding: 0;
    font-size: var(--text-sm);
  }

  a {
    color: var(--color-brand-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  hr {
    border: none;
    border-top: 1px solid var(--color-border);
    margin: var(--space-16) 0;
  }

  strong {
    font-weight: var(--font-semibold);
  }
`;

const Lede = styled.p`
  font-size: var(--text-lg) !important;
  color: var(--color-text-secondary) !important;
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6) !important;
`;

const PurposeNote = styled.p`
  font-size: var(--text-lg) !important;
  font-weight: var(--font-semibold);
  color: var(--color-text-primary) !important;
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-8) !important;
  padding: var(--space-5);
  background: #f5ede0;
  border: 1px solid #e8dcc4;
  border-left: 3px solid #a0522d;
  border-radius: var(--radius-md);
  color: #3a2e1f !important;
`;

// ── Documented performance comparisons ──────────────────────────────────
// Every claim carries its number, its baseline, its methodology, and its
// verification/oracle, so a technical evaluator can skim it as a table.
const ClaimGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  margin: var(--space-8) 0;
`;

const ClaimCard = styled.div`
  padding: var(--space-5);
  background: #f5ede0;
  border: 1px solid #e8dcc4;
  border-radius: var(--radius-md);
`;

const ClaimTitle = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8em;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b7d6b;
  margin-bottom: var(--space-2);
`;

const ClaimHeadline = styled.div`
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
  color: #3a2e1f;
  line-height: 1.35;
  margin-bottom: var(--space-4);
`;

const ClaimFields = styled.dl`
  display: grid;
  grid-template-columns: 7.5rem 1fr;
  gap: var(--space-2) var(--space-4);
  margin: 0;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const FieldLabel = styled.dt`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78em;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8b7d6b;
  padding-top: 0.2em;

  @media (max-width: 560px) {
    margin-top: var(--space-3);
  }
`;

const FieldValue = styled.dd`
  margin: 0;
  font-size: 0.95em;
  color: #3a2e1f;
  line-height: var(--leading-relaxed);

  code {
    background: #fffaf2 !important;
    border-color: #e8dcc4 !important;
    color: #3a2e1f !important;
  }
`;

const ClaimNote = styled.p`
  margin: var(--space-4) 0 0 0 !important;
  padding-top: var(--space-4);
  border-top: 1px solid #e8dcc4;
  font-size: 0.95em !important;
  color: #3a2e1f !important;
`;

const CodeCompare = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6);
  margin: var(--space-8) 0;
`;

const CodeCol = styled.div`
  flex: 1 1 380px;
  min-width: 0;
`;

const CodeLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.8em;
  color: #8b7d6b;
  margin-bottom: var(--space-2);
`;

const CodeBlock = styled.pre`
  margin: 0 !important;
  padding: var(--space-4) !important;
  background: #f5ede0 !important;
  border: 1px solid #e8dcc4 !important;
  border-radius: var(--radius-md);
  overflow-x: auto;
  font-size: 0.85em;
  line-height: 1.5;

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace !important;
    color: #3a2e1f !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    white-space: pre;
    font-size: 1em !important;
  }
`;

const CodeCaption = styled.p`
  font-size: 0.9em !important;
  color: #8b7d6b !important;
  font-style: italic;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: var(--space-6) 0;
  font-size: var(--text-sm);

  th,
  td {
    text-align: left;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--color-border);
  }

  th {
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border-strong);
  }

  td:nth-child(1) {
    font-family: var(--font-mono);
    font-weight: var(--font-medium);
  }

  td:nth-child(2) {
    color: var(--color-text-primary);
  }

  td:nth-child(3) {
    color: var(--color-text-tertiary);
  }
`;

// ── Live inference demo (Stage 6 Inc 3) ─────────────────────────────────
// Streams tokens from the pure-Terra distilgpt2 server at
// terra-serve.data26.solutions (systemd: terra-inference.service on Luna).
const DemoBox = styled.div`
  margin: var(--space-8) 0;
  padding: var(--space-5);
  background: #f5ede0;
  border: 1px solid #e8dcc4;
  border-radius: var(--radius-md);
`;

const DemoControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
`;

const DemoInput = styled.input`
  flex: 1 1 240px;
  min-width: 0;
  padding: var(--space-3) var(--space-4);
  border: 1px solid #d8c8aa;
  border-radius: var(--radius-md);
  background: #fffaf2;
  color: #3a2e1f;
  font-family: var(--font-sans);
  font-size: var(--text-base);

  &:focus {
    outline: none;
    border-color: #a0522d;
  }
`;

const DemoTokens = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: ui-monospace, monospace;
  font-size: 0.8em;
  color: #8b7d6b;

  input {
    width: 56px;
    padding: var(--space-2);
    border: 1px solid #d8c8aa;
    border-radius: var(--radius-sm);
    background: #fffaf2;
    color: #3a2e1f;
    font-family: ui-monospace, monospace;
  }
`;

const DemoButton = styled.button`
  padding: var(--space-3) var(--space-5);
  border: none;
  border-radius: var(--radius-md);
  background: #a0522d;
  color: #fffaf2;
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  cursor: pointer;
  transition: background 150ms ease;

  &:hover:not(:disabled) {
    background: #8a4526;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const DemoOutput = styled.div`
  margin-top: var(--space-4);
  padding: var(--space-4);
  min-height: 3.2em;
  background: #fffaf2;
  border: 1px solid #e8dcc4;
  border-radius: var(--radius-md);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.95em;
  line-height: 1.6;
  color: #3a2e1f;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DemoCursor = styled.span`
  display: inline-block;
  width: 0.55em;
  height: 1.05em;
  margin-left: 1px;
  background: #a0522d;
  vertical-align: text-bottom;
  animation: terra-blink 1s step-end infinite;

  @keyframes terra-blink {
    50% { opacity: 0; }
  }
`;

const DemoHint = styled.p`
  margin: var(--space-3) 0 0 0 !important;
  font-size: var(--text-sm) !important;
  color: #8b7d6b !important;
`;

const DemoError = styled.p`
  margin: var(--space-3) 0 0 0 !important;
  font-size: var(--text-sm) !important;
  color: var(--color-error) !important;
`;

const GateWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: var(--color-background);
`;

const GateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: 320px;
`;

const GateInput = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  font-size: var(--text-base);
  font-family: var(--font-sans);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
  }
`;

const GateButton = styled.button`
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-text-primary);
  color: var(--color-text-inverse);
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  cursor: pointer;
`;

const GateError = styled.p`
  font-size: var(--text-sm);
  color: var(--color-error);
  margin: 0;
`;

const GateHint = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  margin: 0 0 var(--space-2);
  text-align: center;
`;

const STORAGE_KEY = 'terra-unlocked';
const IDENTITY_KEY = 'terra-identity';

interface Identity {
  email: string;
  label: string;
  credentialId: string;
}

// Identity of the recipient who unlocked this session, so a later artifact
// download can be attributed to them.
function readIdentity(): Identity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(IDENTITY_KEY);
    return raw ? (JSON.parse(raw) as Identity) : null;
  } catch {
    return null;
  }
}

function isUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function TerraSizeChart() {
  return (
    <svg
      viewBox="0 0 800 140"
      style={{ width: '100%', maxWidth: '1100px', display: 'block', margin: '2rem auto' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background bar - model weights, sand color */}
      <rect x="40" y="60" width="720" height="40" fill="#e8dcc4" stroke="#8b7d6b" strokeWidth="1" />

      {/* Terra slice - terra clay, minimum visible width */}
      <rect x="40" y="60" width="3" height="40" fill="#a0522d" />

      {/* Callout arrow */}
      <line x1="41.5" y1="60" x2="100" y2="20" stroke="#a0522d" strokeWidth="1.5" />
      <text x="105" y="24" fontSize="13" fill="#3a2e1f" fontFamily="ui-monospace, monospace">
        Terra inference engine — 117 KB
      </text>

      {/* Model weights label */}
      <text x="400" y="86" fontSize="14" fill="#3a2e1f" textAnchor="middle" fontFamily="ui-monospace, monospace">
        distilgpt2 model weights — 328 MB
      </text>

      {/* Scale axis */}
      <line x1="40" y1="115" x2="760" y2="115" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="40" y="130" fontSize="11" fill="#8b7d6b" fontFamily="ui-monospace, monospace">0</text>
      <text x="760" y="130" fontSize="11" fill="#8b7d6b" textAnchor="end" fontFamily="ui-monospace, monospace">329 MB</text>

      {/* Footnote */}
      <text x="400" y="130" fontSize="10" fill="#8b7d6b" textAnchor="middle" fontFamily="ui-monospace, monospace" fontStyle="italic">
        Terra slice rendered at minimum visible width; true proportion 0.034%
      </text>
    </svg>
  );
}

function TerraTimelineChart() {
  return (
    <svg
      viewBox="0 0 1100 200"
      style={{ width: '100%', maxWidth: '1100px', display: 'block', margin: '2rem auto' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* "Two months of C" highlight band - sand color, behind the axis */}
      <rect x="60" y="95" width="374" height="20" fill="#e8dcc4" opacity="0.6" />
      <text x="247" y="88" fontSize="11" fill="#8b7d6b" textAnchor="middle" fontFamily="ui-monospace, monospace" fontStyle="italic">
        Two months of C
      </text>

      {/* Axis line */}
      <line x1="60" y1="105" x2="620" y2="105" stroke="#3a2e1f" strokeWidth="1.5" />

      {/* Axis date labels */}
      <text x="60" y="135" fontSize="11" fill="#8b7d6b" textAnchor="middle" fontFamily="ui-monospace, monospace">Feb 28</text>
      <text x="620" y="135" fontSize="11" fill="#8b7d6b" textAnchor="middle" fontFamily="ui-monospace, monospace">May 11</text>
      <text x="340" y="150" fontSize="11" fill="#8b7d6b" textAnchor="middle" fontFamily="ui-monospace, monospace" fontStyle="italic">72 days</text>

      {/* 02-28 Project begins - leftmost dot with inline label below */}
      <circle cx="60" cy="105" r="5" fill="#3a2e1f" />
      <text x="60" y="175" fontSize="10" fill="#3a2e1f" textAnchor="start" fontFamily="ui-monospace, monospace">
        02-28 Project begins
      </text>

      {/* 04-17 terra1 rewrite - inline label above (still has room) */}
      <circle cx="434" cy="105" r="5" fill="#3a2e1f" />
      <line x1="434" y1="100" x2="434" y2="75" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="434" y="65" fontSize="10" fill="#3a2e1f" textAnchor="middle" fontFamily="ui-monospace, monospace">
        04-17 terra1 rewrite
      </text>

      {/* 04-27 Self-host - INFLECTION POINT, terra clay accent, larger */}
      <circle cx="511" cy="105" r="7" fill="#a0522d" stroke="#3a2e1f" strokeWidth="1.5" />

      {/* Right-cluster dots - 05-02, 05-03, 05-06, 05-10, 05-11 — small dots only, no inline labels */}
      <circle cx="550" cy="105" r="4" fill="#3a2e1f" />
      <circle cx="558" cy="105" r="4" fill="#3a2e1f" />
      <circle cx="581" cy="105" r="4" fill="#3a2e1f" />
      <circle cx="612" cy="105" r="4" fill="#3a2e1f" />
      <circle cx="620" cy="105" r="6" fill="#a0522d" stroke="#3a2e1f" strokeWidth="1.5" />

      {/* Callout panel: 04-27 Self-host (special, emphasized) */}
      <line x1="511" y1="112" x2="700" y2="35" stroke="#a0522d" strokeWidth="1" />
      <text x="710" y="32" fontSize="11" fill="#a0522d" fontFamily="ui-monospace, monospace" fontWeight="bold">
        04-27 Self-host
      </text>
      <text x="710" y="46" fontSize="10" fill="#8b7d6b" fontFamily="ui-monospace, monospace" fontStyle="italic">
        Stage 2 ≡ Stage 3, C work ends
      </text>

      {/* Callout panel: 05-02 DistilBERT */}
      <line x1="550" y1="105" x2="700" y2="68" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="710" y="72" fontSize="11" fill="#3a2e1f" fontFamily="ui-monospace, monospace">
        05-02 DistilBERT inference
      </text>

      {/* Callout panel: 05-03 distilgpt2 */}
      <line x1="558" y1="105" x2="700" y2="92" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="710" y="96" fontSize="11" fill="#3a2e1f" fontFamily="ui-monospace, monospace">
        05-03 distilgpt2 inference
      </text>

      {/* Callout panel: 05-06 Stage A */}
      <line x1="581" y1="105" x2="700" y2="116" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="710" y="120" fontSize="11" fill="#3a2e1f" fontFamily="ui-monospace, monospace">
        05-06 Stage A closes
      </text>

      {/* Callout panel: 05-10 Mach-O self-host */}
      <line x1="612" y1="105" x2="700" y2="140" stroke="#8b7d6b" strokeWidth="0.5" />
      <text x="710" y="144" fontSize="11" fill="#3a2e1f" fontFamily="ui-monospace, monospace">
        05-10 Mach-O self-host
      </text>

      {/* Callout panel: 05-11 Ships (special, emphasized) */}
      <line x1="620" y1="105" x2="700" y2="164" stroke="#a0522d" strokeWidth="1" />
      <text x="710" y="168" fontSize="11" fill="#a0522d" fontFamily="ui-monospace, monospace" fontWeight="bold">
        05-11 inference.dmg ships
      </text>
    </svg>
  );
}

// Pure-Terra distilgpt2 inference server (TLS-terminated at Caddy; the upstream
// is a static x86-64 ELF with no libc/runtime). A plain text/plain POST keeps
// this a CORS "simple request" (no preflight); the server streams the prompt
// echo + generated tokens one-by-one over a close-delimited HTTP response.
const INFERENCE_ENDPOINT = 'https://terra-serve.data26.solutions';

// The greedy model can emit long runs of newline tokens, and the server appends
// a trailing newline; rendered with `white-space: pre-wrap` that becomes a stack
// of blank lines. Collapse blank-line runs and trim trailing whitespace for
// display only — the bytes on the wire are untouched.
function normalizeOutput(s: string): string {
  return s
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+$/g, '');
}

function LiveDemo() {
  const [prompt, setPrompt] = useState('The history of coffee');
  const [tokens, setTokens] = useState(20);
  const [output, setOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setOutput('');
    setError('');

    const n = Math.max(1, Math.min(30, Math.floor(tokens) || 20));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);

    try {
      const res = await fetch(`${INFERENCE_ENDPOINT}/?n=${n}`, {
        method: 'POST',
        body: text,
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        setError('Server busy — please try again in a moment.');
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setOutput(acc);
      }
      acc += decoder.decode();
      setOutput(acc);
      if (acc.trim().length === 0) {
        setError('No output for that prompt — try a shorter or simpler one.');
      }
    } catch (e) {
      const aborted = e instanceof DOMException && e.name === 'AbortError';
      setError(aborted ? 'Server busy — please try again.' : 'Could not reach the inference server.');
    } finally {
      clearTimeout(timer);
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      generate();
    }
  };

  return (
    <DemoBox>
      <DemoControls>
        <DemoInput
          type="text"
          value={prompt}
          maxLength={120}
          placeholder="Type a prompt…"
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        <DemoTokens>
          tokens
          <input
            type="number"
            min={1}
            max={30}
            value={tokens}
            onChange={(e) => setTokens(Number(e.target.value))}
            disabled={busy}
          />
        </DemoTokens>
        <DemoButton type="button" onClick={generate} disabled={busy}>
          {busy ? 'Generating…' : 'Generate'}
        </DemoButton>
      </DemoControls>

      <DemoOutput>
        {normalizeOutput(output)}
        {busy && <DemoCursor />}
      </DemoOutput>

      {error ? (
        <DemoError>{error}</DemoError>
      ) : (
        <DemoHint>
          Streamed live from a pure-Terra distilgpt2 binary on bare metal — no Python,
          no runtime. Each token appears the instant it is argmax&apos;d (~7 tok/s, one
          request at a time). It is an 82M-parameter model decoded greedily, so the output
          is fully deterministic and can repeat — the proof here is the engine, not the prose.
        </DemoHint>
      )}
    </DemoBox>
  );
}

export function Terra() {
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isUnlocked()) setUnlocked(true);
  }, []);

  const unlock = (identity: Identity) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
      window.sessionStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
    } catch {
      /* sessionStorage unavailable */
    }
    setUnlocked(true);
    setError(false);
  };

  // Attribute an artifact download to whoever unlocked this session.
  const handleDownload = () => {
    const id = readIdentity();
    void recordAccess({
      email: id?.email ?? 'unknown',
      label: id?.label ?? '',
      credentialId: id?.credentialId ?? 'unknown',
      success: true,
      event: 'download',
    });
    // No preventDefault — the browser proceeds with the download.
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(false);

    try {
      // Per-recipient credential: validates against Firestore and identifies
      // who is opening the page so the access can be logged against them.
      const result = await validateCredential(email, password);
      if (result.ok) {
        const identity = {
          email: result.email ?? email.trim().toLowerCase(),
          label: result.label ?? '',
          credentialId: result.id,
        };
        await recordAccess({ ...identity, success: true });
        unlock(identity);
        return;
      }

      // Master fallback — a single env password that always works, so we can
      // never be locked out before credentials are issued. Logged as 'master'.
      const master = import.meta.env.VITE_TERRA_PASSWORD;
      if (master && password === master) {
        const identity = {
          email: email.trim().toLowerCase() || 'master',
          label: 'master',
          credentialId: 'master',
        };
        await recordAccess({ ...identity, success: true });
        unlock(identity);
        return;
      }

      // Failed attempt — record it (best-effort) so probing is visible too.
      await recordAccess({
        email: email.trim().toLowerCase(),
        label: '',
        credentialId: result.id,
        success: false,
      });
      setError(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!unlocked) {
    return (
      <>
        <PageHead
          title="Terra"
          description="Terra."
          noindex
        />
        <GateWrapper>
          <GateForm onSubmit={handleSubmit}>
            <GateHint>This page is private. Enter the email and password it was sent to.</GateHint>
            <GateInput
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(false);
              }}
              placeholder="Email"
              autoComplete="off"
              autoFocus
            />
            <GateInput
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(false);
              }}
              placeholder="Password"
              autoComplete="off"
            />
            <GateButton type="submit" disabled={submitting}>
              {submitting ? 'Checking…' : 'Enter'}
            </GateButton>
            {error && <GateError>That email and password don&apos;t match.</GateError>}
          </GateForm>
        </GateWrapper>
      </>
    );
  }

  return (
    <PageWrapper>
      <PageHead
        title="Terra"
        description="A systems language for AI-authored bare-metal code."
        noindex
      />
      <Article>
        <h1>Terra</h1>

        <PurposeNote>
          This page is a proof surface for technical evaluation of Terra —
          verified performance results, methodology, and downloadable artifacts.
          It is not a first-contact pitch.
        </PurposeNote>

        <Lede>
          A systems language for AI-authored bare-metal code — and a proven method
          for producing verified software for architectures that have no toolchain yet.
        </Lede>

        <p>
          No runtime, no libc, no abstraction tax — the path leads to the metal itself.
          Terra is shaped for how AI agents actually write code, targeting the binaries
          C was built for. The payoff is at scale: on large, sustained systems work, the
          manual-memory and lifetime burden of conventional systems languages compounds
          across the codebase and dominates cost. Terra removes that burden — which is
          why work that took months of grinding in C became tractable in days once Terra
          existed (see the timeline below).
        </p>

        <h2>Running live, right now</h2>

        <p>
          The text below streams from a pure-Terra distilgpt2 binary on bare metal —
          no Python, no PyTorch, no runtime, no libc, served as a static x86-64 ELF
          over HTTPS. distilgpt2 is small (82M parameters), chosen here because it
          responds instantly; the output is deterministic greedy decoding, so it can
          repeat. The point of this widget is simply that a real neural network is
          running live, in your browser, from a static bare-metal binary. The
          <em> quality</em> demo is the 7-billion-parameter model below; the
          <em> run-it-yourself</em> proof is the downloadable artifact.
        </p>

        <LiveDemo />

        <h3>The same engine runs a 7-billion-parameter model on bare metal</h3>

        <p>
          The identical Terra inference engine runs Mistral-7B — a real, full-size
          language model — as a static native binary with no GPU, no runtime, and no
          Python. Its output is <strong>bit-exact to the reference implementation</strong>{' '}
          (verified token-for-token against HuggingFace, deterministic across runs). On
          CPU alone it generates at roughly 1.8 seconds per token — not instant, because
          nothing is accelerating it but the silicon itself. That is the point: a
          coherent 7B model running with zero abstraction beneath it.
        </p>

        <p>
          Sample (Mistral-7B, verbatim, bit-exact to reference): <em>&ldquo;The capital
          of France is a city of many faces. It is a city of history, culture, art,
          fashion, and…&rdquo;</em>
        </p>

        <hr />

        <h2>Transfer to silicon that never existed</h2>

        <p>
          The obvious question about any AI-generated systems code: is it just
          recalling training data? We removed that possibility entirely. We invented
          a 32-bit instruction set that never existed, and had the AI build a complete
          compiler backend for it — lexer, parser, register allocator, instruction
          selection, encoding, branch resolution — from the written spec alone. Then we
          ran four hard systems primitives on this invented architecture, each verified
          against an external published standard:
        </p>

        <ResultsTable>
          <thead>
            <tr>
              <th>Primitive</th>
              <th>Result on the invented ISA</th>
              <th>External oracle</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>SHA-256</td>
              <td>234/234 (incl. 100k-chain Monte Carlo)</td>
              <td>NIST</td>
            </tr>
            <tr>
              <td>AES-256</td>
              <td>812/812 KAT</td>
              <td>NIST</td>
            </tr>
            <tr>
              <td>DEFLATE</td>
              <td>host-gzip interop</td>
              <td>live gzip</td>
            </tr>
            <tr>
              <td>JSON</td>
              <td>283/283</td>
              <td>JSONTestSuite</td>
            </tr>
          </tbody>
        </ResultsTable>

        <p>
          Four computational shapes, four independent oracles, on hardware that did not
          exist until we wrote the spec. The capability can&apos;t be recall — the target
          never existed. The correctness isn&apos;t self-asserted — it&apos;s checked
          against NIST and other external standards. The backend matured into a general
          compiler as it went, and empirically discovered an under-documented hardware
          quirk in the invented architecture by probing its behavior — a capability
          we&apos;ve seen repeat across multiple invented architectures, and exactly what
          real novel silicon demands, where documentation is always incomplete.
        </p>

        <h3>What the hard code actually looks like</h3>

        <p>
          This is the actual mathematical core of AES — the S-box computed live from
          GF(2⁸) field arithmetic, not a pasted lookup table. AI-authored, clean-room
          from the FIPS-197 standard, and verified against every NIST vector. The S-box
          being <em>computed</em> rather than tabulated is the honest-hard way: there is
          no constant table to copy, only the field math that defines it.
        </p>

        <CodeBlock><code>{`// Multiply in GF(2^8) — the finite field AES is defined over.
fn gf_mul(a_in: u32, b_in: u32): u32 {
  let p: u32 = 0
  let a: u32 = a_in & 255
  let b: u32 = b_in & 255
  let i: u32 = 0
  while (i < 8) {
    if ((b & 1) == 1) { p = p ^ a }
    let hi: u32 = a & 128
    a = (a << 1) & 255
    if (hi == 128) { a = a ^ 27 }   // 0x1B field reduction
    b = b >> 1
    i = i + 1
  }
  return p & 255
}

// Multiplicative inverse in GF(2^8) is a^254. inv(0) := 0 by AES convention.
fn gf_inv(a: u32): u32 {
  if (a == 0) { return 0 }
  let result: u32 = 1
  let i: u32 = 0
  while (i < 254) { result = gf_mul(result, a)  i = i + 1 }
  return result
}

// 8-bit rotate-left, used by the S-box affine transform.
fn rotl8(x: u32, n: u32): u32 {
  let xx: u32 = x & 255
  return ((xx << n) | (xx >> (8 - n))) & 255
}

// Build the forward S-box from field arithmetic:
//   s = b ^ rotl(b,1) ^ rotl(b,2) ^ rotl(b,3) ^ rotl(b,4) ^ 0x63
// where b = multiplicative-inverse(i) in GF(2^8).
fn build_sboxes(base: u64): void {
  let sbox: u64 = base + 0
  let isbox: u64 = base + 256
  let i: u32 = 0
  while (i < 256) {
    let b: u32 = gf_inv(i)
    let s: u32 = b ^ rotl8(b, 1) ^ rotl8(b, 2) ^ rotl8(b, 3) ^ rotl8(b, 4) ^ 99
    s = s & 255
    wb(sbox + (i as u64), s as u8)     // forward S-box
    wb(isbox + (s as u64), i as u8)    // inverse = inverse permutation
    i = i + 1
  }
  return
}`}</code></CodeBlock>

        <CodeCaption>
          AES-256 in Terra: 1,630/1,630 NIST known-answer tests, both directions. The
          S-box here is derived, not copied — exactly the kind of from-scratch systems
          code that can&apos;t come from recall.
        </CodeCaption>

        <hr />

        <h2>Documented performance comparisons</h2>

        <p>
          Terra&apos;s GPU work targets the Metal layer beneath MLX, so MLX is both
          the oracle and the ceiling: every claim below is measured against it on
          the same machine, and correctness is gated before speed is reported.
          Unless noted, the workload is Llama-3.2-1B greedy decode, 25 tokens, on
          an Apple M5 (16 GB), with a warmup pass before every wall-clock
          measurement.
        </p>

        <ClaimGrid>
          <ClaimCard>
            <ClaimTitle>A — Native GPU dispatch</ClaimTitle>
            <ClaimHeadline>
              ~25× cheaper kernel-submission cost than MLX&apos;s Python custom-kernel
              shim.
            </ClaimHeadline>
            <ClaimFields>
              <FieldLabel>Number</FieldLabel>
              <FieldValue>~25× cheaper submission cost.</FieldValue>

              <FieldLabel>Baseline</FieldLabel>
              <FieldValue>MLX&apos;s Python custom-kernel shim.</FieldValue>

              <FieldLabel>Methodology</FieldLabel>
              <FieldValue>
                Batched submit-only wall-clock timing, with compute isolated out —
                the measurement is dispatch overhead alone.
              </FieldValue>

              <FieldLabel>Verification</FieldLabel>
              <FieldValue>
                Measured <strong>27.5 µs → 1.09 µs per submit</strong> on an Apple
                M5. Terra submits natively, with no Python in the inference path.
                (Phase 8, M2.)
              </FieldValue>
            </ClaimFields>
          </ClaimCard>

          <ClaimCard>
            <ClaimTitle>B — fp32 decode</ClaimTitle>
            <ClaimHeadline>
              19.72 tok/s — 0.79× of MLX-fp32, token-exact.
            </ClaimHeadline>
            <ClaimFields>
              <FieldLabel>Number</FieldLabel>
              <FieldValue>
                <strong>19.72 tok/s</strong>, which is <strong>0.79×</strong> of
                MLX-fp32 (MLX-fp32: 24.93 tok/s on the same hardware).
              </FieldValue>

              <FieldLabel>Baseline</FieldLabel>
              <FieldValue>
                MLX-fp32 on the same Apple M5 (16 GB), same model, same weights —
                the unsloth mirror of Llama-3.2-1B, one checkpoint feeding both
                Terra and the oracle.
              </FieldValue>

              <FieldLabel>Methodology</FieldLabel>
              <FieldValue>
                Warmup before wall-clock, greedy decode, 25 tokens, fp32
                throughout.
              </FieldValue>

              <FieldLabel>Verification</FieldLabel>
              <FieldValue>
                <strong>Token-exact vs MLX greedy — 25/25 tokens identical.</strong>{' '}
                The path runs at ~97 GB/s, roughly 80% of the M5&apos;s ~120 GB/s
                bandwidth peak. (Phase 8, M3.5.)
              </FieldValue>
            </ClaimFields>
          </ClaimCard>

          <ClaimCard>
            <ClaimTitle>C — 4-bit decode</ClaimTitle>
            <ClaimHeadline>
              ~140 tok/s — ~6× MLX-fp32 (cross-precision), and parity within
              thermal noise with MLX-4bit.
            </ClaimHeadline>
            <ClaimFields>
              <FieldLabel>Number</FieldLabel>
              <FieldValue>
                <strong>~140 tok/s</strong>. That is <strong>~6× MLX-fp32</strong>{' '}
                — a <strong>cross-precision</strong> comparison (Terra 4-bit vs MLX
                fp32) — and <strong>parity within thermal noise</strong> against
                MLX-4bit at matching precision.
              </FieldValue>

              <FieldLabel>Baseline</FieldLabel>
              <FieldValue>
                MLX-fp32 (~22.2 tok/s re-measured; swings 21.7–24.9 thermally) and
                MLX-4bit (~140 tok/s), both on the same Apple M5.
              </FieldValue>

              <FieldLabel>Methodology</FieldLabel>
              <FieldValue>
                Warmup, greedy decode, 25 tokens. Quantized weights are streamed
                and dequantized in-register at the MMA — never materialized to fp32
                in memory. Correctness is judged by a same-precision oracle: Terra
                4-bit vs MLX 4-bit.
              </FieldValue>

              <FieldLabel>Verification</FieldLabel>
              <FieldValue>
                <strong>
                  Token-exact vs the 4-bit MLX oracle — 25/25 tokens identical.
                </strong>{' '}
                The quant path is verified against <code>mx.dequantize</code> to
                2.4e-7, and Terra consumes the identical <code>mx.quantize</code>{' '}
                weights the oracle uses. (Phase 9, M2 — final, 4-accumulator qmv.)
              </FieldValue>
            </ClaimFields>
            <ClaimNote>
              <strong>Read this claim precisely.</strong> The same-precision result
              is <strong>parity within thermal noise</strong> — the ratio straddles
              1.0 across back-to-back runs — not a clean sustained beat of MLX-4bit.
              The ~6× figure is <strong>cross-precision</strong>: Terra 4-bit
              measured against MLX-fp32. Neither number is rounded up.
            </ClaimNote>
          </ClaimCard>

          <ClaimCard>
            <ClaimTitle>D — Authorship</ClaimTitle>
            <ClaimHeadline>
              13 Terra-authored Metal kernels drive the forward pass. No Python in
              the inference path.
            </ClaimHeadline>
            <ClaimFields>
              <FieldLabel>Number</FieldLabel>
              <FieldValue>
                <strong>13 Terra-authored Metal kernels</strong> make up the
                Llama-3.2-1B forward pass on the Apple GPU.
              </FieldValue>

              <FieldLabel>Baseline</FieldLabel>
              <FieldValue>
                Hand-written Metal (MSL) — the layer every custom-kernel effort on
                Apple Silicon writes by hand today.
              </FieldValue>

              <FieldLabel>Methodology</FieldLabel>
              <FieldValue>
                <code>forward.metal</code> is emitted by the Terra compiler from{' '}
                <code>forward_msl.terra</code>. No kernel in the inference path was
                hand-written, including through the entire optimization arc.
              </FieldValue>

              <FieldLabel>Verification</FieldLabel>
              <FieldValue>
                <code>forward.metal</code> re-emits{' '}
                <strong>byte-identical</strong> from{' '}
                <code>forward_msl.terra</code> — the &quot;Terra authored the
                kernel&quot; claim survives every optimization above precisely
                because no kernel was hand-written at any rung.
              </FieldValue>
            </ClaimFields>
          </ClaimCard>
        </ClaimGrid>

        <hr />

        <h2>Artifact</h2>

        <h3>distilgpt2 inference — macOS arm64</h3>

        <p>
          This is the run-it-yourself proof: a complete transformer language model
          packaged with its weights for direct distribution — download it, mount it,
          and run a real language model on your own machine. Prompt the binary, watch
          tokens stream out — a token-exact match against the PyTorch reference, in a
          117 KB static binary written in Terra.
        </p>

        <TerraSizeChart />

        <p>Composition of the mounted DMG (~329 MB uncompressed):</p>

        <ul>
          <li><strong>Terra inference binary:</strong> 117,312 bytes — entire transformer engine</li>
          <li><strong>distilgpt2 model weights</strong> (FP32): ~328 MB — the model itself</li>
          <li><strong>Tokenizer + config + verification fixture:</strong> ~600 KB</li>
        </ul>

        <p>
          Everything outside the 117 KB binary is the model. The boundary is exact.
        </p>

        <p>
          <a href="/inference.dmg" download onClick={handleDownload}>Download inference.dmg — 308 MB</a>
        </p>

        <p>After mounting:</p>

        <pre><code>/Volumes/TerraInference/run.sh "your prompt here" 10</code></pre>

        <p>
          Signed, notarized, and stapled — no Gatekeeper warnings. Produces a
          token-exact match against PyTorch's reference implementation.
        </p>

        <hr />

        <h2>Verifying the work</h2>

        <p>The artifact is reproducible from source and verifiable independently.</p>

        <p>
          The Terra compiler self-hosts: stage2 and stage3 produce byte-identical
          output (sha256 <code>30b40845…b209a</code>). The compiler compiles itself.
        </p>

        <p>
          That fixed point is maintained, not just achieved once. After a recent
          compiler-defect fix, the self-host re-closed byte-identical on both x86-64 and
          ARM64 — a mature find-fix-reverify loop, not a one-time milestone.
        </p>

        <p>
          The inference binary's output is deterministic: same input produces the
          same token sequence, every run, matching PyTorch's reference implementation
          token-for-token across the generated IDs.
        </p>

        <p>
          The signed DMG passes <code>spctl -a -vvv -t exec</code> cleanly on a
          quarantined copy — the recipient experience is verified, not assumed.
        </p>

        <p>
          Stage 0 — the original ~7,500-line bootstrap compiler — is written in C
          and remains in the toolchain. Everything past Stage 0 is Terra. The
          boundary is not arbitrary.
        </p>

        <hr />

        <h2>Reading Terra</h2>

        <p>
          Here is a real function from Terra's standard library, beside the same
          function in TypeScript. Same shape, same control flow, same operators —
          a TypeScript developer reads and writes Terra with almost no ramp.
        </p>

        <CodeCompare>
          <CodeCol>
            <CodeLabel>Terra — std/math.terra</CodeLabel>
            <CodeBlock><code>{`/// Check if a value is a power of 2.
export fn is_power_of_2(value: u64): bool {
  if (value == 0) { return false }
  return (value & (value - 1)) == 0
}

/// Compute the next power of 2 >= value.
export fn next_power_of_2(value: u64): u64 {
  if (value == 0) { return 1 }
  if (is_power_of_2(value)) { return value }

  // Fill all bits below the highest set bit, then add 1
  let v: u64 = value - 1
  v = v | (v >> 1)
  v = v | (v >> 2)
  v = v | (v >> 4)
  v = v | (v >> 8)
  v = v | (v >> 16)
  v = v | (v >> 32)
  return v + 1
}`}</code></CodeBlock>
          </CodeCol>

          <CodeCol>
            <CodeLabel>TypeScript</CodeLabel>
            <CodeBlock><code>{`function isPowerOf2(value: bigint): boolean {
  if (value === 0n) return false;
  return (value & (value - 1n)) === 0n;
}

function nextPowerOf2(value: bigint): bigint {
  if (value === 0n) return 1n;
  if (isPowerOf2(value)) return value;

  // Fill all bits below the highest set bit, then add 1
  let v: bigint = value - 1n;
  v = v | (v >> 1n);
  v = v | (v >> 2n);
  v = v | (v >> 4n);
  v = v | (v >> 8n);
  v = v | (v >> 16n);
  v = v | (v >> 32n);
  return v + 1n;
}`}</code></CodeBlock>
          </CodeCol>
        </CodeCompare>

        <CodeCaption>
          TypeScript needs <code>bigint</code> for correct 64-bit math; Terra has
          a native <code>u64</code>. Terra is a systems language with a small,
          explicit standard library — not a drop-in for the TypeScript ecosystem.
        </CodeCaption>

        <hr />

        <h2>Timeline</h2>

        <TerraTimelineChart />

        <p><strong>2026-02-28</strong> Project begins. Stage 0 compiler started in C.</p>

        <p>
          <strong>2026-02-28 → 2026-04-27</strong> Stage 0 plus first terra1.terra
          attempts. Two months of AI agents grinding through ~7,500 lines of C — every
          Terra-source change cascading back into hand-maintained C edits. The cost
          wasn&apos;t any single hard problem; it was the manual-memory and lifetime
          burden compounding across a growing codebase. Once Terra could compile itself,
          that burden was gone — and the same class of work that took two months in C
          took days.
        </p>

        <p>
          <strong>2026-04-17</strong> terra1.terra rewritten with a 3-phase
          architecture. Stage 0 retained as-is.
        </p>

        <p>
          <strong>2026-04-27</strong> Self-host verified. Stage 2 and Stage 3 produce
          byte-identical output. The C work ends here.
        </p>

        <p>
          <strong>2026-05-02</strong> — 5 days later. End-to-end DistilBERT inference,
          token-verified against PyTorch.
        </p>

        <p>
          <strong>2026-05-03</strong> — 6 days. distilgpt2 generative inference running.
        </p>

        <p>
          <strong>2026-05-06</strong> — 9 days. Stage A closed. Token-exact match
          against PyTorch (9/9 IDs). First Mach-O hello-world the same day.
        </p>

        <p>
          <strong>2026-05-10</strong> — 13 days. Mach-O self-host byte-identical.
        </p>

        <p>
          <strong>2026-05-11</strong> — 14 days. inference.dmg ships, signed and
          notarized for native macOS distribution.
        </p>

        <hr />

        <h2>Contact</h2>

        <p>Isaac Long<br/>
        isaaclong@lancastersystemsautomation.com</p>

        <p>
          Terra is developed by Lancaster Systems Automation, a brand of Data 26
          Solutions LLC (a Pennsylvania software company).{' '}
          <a href="https://lancastersystemsautomation.com/">
            lancastersystemsautomation.com
          </a>
        </p>
      </Article>
    </PageWrapper>
  );
}

export default Terra;
